import { type SkinnedMesh } from 'three'
import { MediaPipePoseProvider } from './MediaPipePoseProvider'
import { type PoseProvider } from './PoseProvider'
import { PoseToRig } from './PoseToRig'

/**
 * OfflineVideoMocapProcessor
 *
 * Drives PoseToRig from a pre-recorded video file instead of a live webcam.
 * Iterates the video frame-by-frame at a target FPS, runs MediaPipe inference
 * on each frame, and fires a per-frame callback so the caller can bake
 * keyframes into a clip — same downstream contract as the live mocap path,
 * just with a deterministic frame source instead of requestAnimationFrame.
 *
 * The processing pace is bounded by inference speed, not real-time playback
 * speed; on a fast GPU a 30-second clip processes in well under 30 seconds.
 *
 * Cancellation: pass an `abort_signal`. The loop checks between frames and
 * resolves early without calling the completion path.
 */

export interface OfflineVideoMocapOptions {
  file: File
  skinned_mesh: SkinnedMesh
  /** Target FPS to sample the video at. Default 30 — matches typical webcam mocap timing. */
  fps?: number
  /** Bone whose XZ position is driven from the hip-midpoint landmark when root motion is enabled. */
  root_motion_bone_name?: string
  /** Initial root motion enable. Defaults to false (consistent with live mocap default). */
  root_motion_enabled?: boolean
  /** Disabled bone names. */
  disabled_bones?: string[]
  /** Initial confidence-weighted smoothing strength. */
  smoothing_strength?: number
  /**
   * Per-frame hook fired AFTER PoseToRig has updated the skeleton. Caller
   * snapshots the bone state and writes keyframes into the target clip
   * (matches the live `on_pose_update` contract).
   */
  on_pose_update?: (frame_time_seconds: number) => void
  /** Progress callback: called with `{ done, total }` frame counts. */
  on_progress?: (progress: { frame: number, total_frames: number, percent: number }) => void
  /** AbortSignal for cancellation. */
  abort_signal?: AbortSignal
  /** Optional override of the pose-detection backend. Defaults to MediaPipe. */
  provider?: PoseProvider
}

export interface OfflineVideoMocapResult {
  /** Total seconds of video processed. Equals last frame's time. */
  duration_seconds: number
  /** Number of frames where inference succeeded (a pose was detected). */
  frames_with_pose: number
  /** Total frames stepped (including those where inference returned null). */
  frames_stepped: number
  /** True if processing was aborted before the last frame. */
  aborted: boolean
}

export class OfflineVideoMocapProcessor {
  static async process (options: OfflineVideoMocapOptions): Promise<OfflineVideoMocapResult> {
    const fps = options.fps ?? 30
    if (fps <= 0 || !Number.isFinite(fps)) {
      throw new Error('OfflineVideoMocapProcessor: fps must be a positive finite number.')
    }

    const provider = options.provider ?? new MediaPipePoseProvider()
    const video = await create_video_from_file(options.file)
    const pose_to_rig = new PoseToRig({
      root_motion_bone_name: options.root_motion_bone_name,
      smoothing_strength: options.smoothing_strength
    })

    let frames_with_pose = 0
    let frames_stepped = 0
    let aborted = false

    try {
      await provider.init()
      pose_to_rig.bind(options.skinned_mesh.skeleton)
      if (!pose_to_rig.is_bound()) {
        throw new Error('No driveable bones found on this skeleton — mocap requires a humanoid rig.')
      }
      if (options.disabled_bones !== undefined) {
        pose_to_rig.set_disabled_bones(options.disabled_bones)
      }
      if (options.root_motion_enabled === true) {
        pose_to_rig.set_root_motion_enabled(true)
      }

      const duration = video.duration
      if (!Number.isFinite(duration) || duration <= 0) {
        throw new Error('Could not read video duration. The file may be unsupported or corrupted.')
      }
      const total_frames = Math.max(1, Math.floor(duration * fps))

      for (let frame = 0; frame < total_frames; frame++) {
        if (options.abort_signal?.aborted === true) {
          aborted = true
          break
        }

        const target_time = Math.min(duration, frame / fps)
        await seek_video(video, target_time)

        const landmarks = await provider.detect(video)
        if (landmarks !== null) {
          pose_to_rig.update(landmarks, target_time)
          frames_with_pose++
          options.on_pose_update?.(target_time)
        }
        frames_stepped++
        options.on_progress?.({
          frame: frames_stepped,
          total_frames,
          percent: (frames_stepped / total_frames) * 100
        })
      }

      return {
        duration_seconds: duration,
        frames_with_pose,
        frames_stepped,
        aborted
      }
    } finally {
      // Always release resources, even on error/abort.
      try { await provider.dispose() } catch (_) { /* best effort */ }
      try {
        if (video.src.startsWith('blob:')) URL.revokeObjectURL(video.src)
        video.removeAttribute('src')
        if (!is_jsdom()) video.load()
      } catch (_) { /* best effort */ }
    }
  }
}

function is_jsdom (): boolean {
  return typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('jsdom')
}

/**
 * Build a hidden HTMLVideoElement from a File. Awaits `loadedmetadata` so the
 * caller can read `duration` and seek immediately.
 */
async function create_video_from_file (file: File): Promise<HTMLVideoElement> {
  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'
  video.crossOrigin = 'anonymous'
  video.style.display = 'none'
  document.body.appendChild(video)
  const url = URL.createObjectURL(file)
  video.src = url
  await new Promise<void>((resolve, reject) => {
    const on_loaded = (): void => {
      video.removeEventListener('loadedmetadata', on_loaded)
      video.removeEventListener('error', on_error)
      resolve()
    }
    const on_error = (): void => {
      video.removeEventListener('loadedmetadata', on_loaded)
      video.removeEventListener('error', on_error)
      reject(new Error('Failed to load video file. Unsupported format or corrupted data.'))
    }
    video.addEventListener('loadedmetadata', on_loaded)
    video.addEventListener('error', on_error)
  })
  return video
}

/**
 * Seek the video to `target_time` and await the `seeked` event. Resolves once
 * the new frame is decoded and ready for canvas/MediaPipe consumption.
 */
async function seek_video (video: HTMLVideoElement, target_time: number): Promise<void> {
  if (Math.abs(video.currentTime - target_time) < 1e-4) return
  await new Promise<void>((resolve, reject) => {
    const on_seeked = (): void => {
      video.removeEventListener('seeked', on_seeked)
      video.removeEventListener('error', on_error)
      resolve()
    }
    const on_error = (): void => {
      video.removeEventListener('seeked', on_seeked)
      video.removeEventListener('error', on_error)
      reject(new Error(`Failed to seek video to ${target_time.toFixed(3)}s.`))
    }
    video.addEventListener('seeked', on_seeked)
    video.addEventListener('error', on_error)
    video.currentTime = target_time
  })
}
